import { Project } from "@/integrations/supabase/types/";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Plus, CheckCircle2, Circle, Calendar as CalendarIcon, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ProjectMilestonesProps {
  project: Project;
  onUpdate: () => void;
}

export const ProjectMilestones = ({ project, onUpdate }: ProjectMilestonesProps) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    title: "",
    description: "",
    due_date: new Date()
  });
  const queryClient = useQueryClient();

  const { data: milestones, isLoading } = useQuery({
    queryKey: ["project-milestones", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_milestones")
        .select("*")
        .eq("project_id", project.id)
        .order("due_date", { ascending: true });

      if (error) throw error;
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (milestone: typeof newMilestone) => {
      const { error } = await supabase
        .from("project_milestones")
        .insert([{
          project_id: project.id,
          title: milestone.title,
          description: milestone.description,
          due_date: format(milestone.due_date, "yyyy-MM-dd")
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-milestones", project.id] });
      toast.success("Milestone created successfully");
      setIsAddOpen(false);
      setNewMilestone({ title: "", description: "", due_date: new Date() });
      onUpdate();
    },
    onError: () => {
      toast.error("Failed to create milestone");
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("project_milestones")
        .update({ 
          status,
          completed_at: status === "completed" ? new Date().toISOString() : null
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-milestones", project.id] });
      toast.success("Milestone updated");
      onUpdate();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("project_milestones")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-milestones", project.id] });
      toast.success("Milestone deleted");
      onUpdate();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMilestone.title) return;
    createMutation.mutate(newMilestone);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500/20 text-green-700 dark:text-green-300";
      case "in_progress": return "bg-blue-500/20 text-blue-700 dark:text-blue-300";
      default: return "bg-gray-500/20 text-gray-700 dark:text-gray-300";
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading milestones...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Project Milestones</h3>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Milestone
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Milestone</DialogTitle>
              <DialogDescription>
                Add a project milestone with title, description, and due date
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  placeholder="Milestone title"
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Textarea
                  placeholder="Description (optional)"
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(newMilestone.due_date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newMilestone.due_date}
                      onSelect={(date) => date && setNewMilestone({ ...newMilestone, due_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Milestone"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {milestones && milestones.length > 0 ? (
          milestones.map((milestone) => (
            <Card key={milestone.id} className="glass-card p-4">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: milestone.id,
                    status: milestone.status === "completed" ? "pending" : "completed"
                  })}
                  className="mt-1"
                >
                  {milestone.status === "completed" ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className={cn(
                        "font-medium",
                        milestone.status === "completed" && "line-through text-muted-foreground"
                      )}>
                        {milestone.title}
                      </h4>
                      {milestone.description && (
                        <p className="text-sm text-muted-foreground mt-1">{milestone.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate(milestone.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getStatusColor(milestone.status)}>
                      {milestone.status.replace("_", " ")}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Due: {format(new Date(milestone.due_date), "MMM dd, yyyy")}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="glass-card p-8 text-center">
            <p className="text-muted-foreground">No milestones yet. Add your first milestone to track progress.</p>
          </Card>
        )}
      </div>
    </div>
  );
};
